import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import useNotifier from "@saleor/hooks/useNotifier";
import { OrderDetails_order } from "@saleor/motherOrders/types/OrderDetails";
import { useRemittanceRunnerManualAdjustment } from "@saleor/remittanceRunner/mutations";
import { useDialogStyles } from "@saleor/remittanceRunner/styles/dialogStyles";
import { isOrderManuallyAdjusted } from "@saleor/remittanceRunner/utils/handlers";
import { DialogProps } from "@saleor/types";
import { currencyFormat } from "@saleor/utils/formatters/currencyFormat";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { RemittanceManualAdjustmentDropdown } from "../RemittanceManualAdjustmentDropdown/RemittanceManualAdjustmentDropdown";

export interface RemittanceManualAdjustDialogProps extends DialogProps {
  order: OrderDetails_order;
  refetchOrder: (variables?: any) => Promise<any>;
}

const RemittanceManualAdjustDialog: React.FC<RemittanceManualAdjustDialogProps> = props => {
  const { onClose, open, order, refetchOrder } = props;

  const classes = useDialogStyles(props);
  const intl = useIntl();
  const notify = useNotifier();

  const [forDeduct, setForDeduct] = useState(true);
  const [formAmount, setFormAmount] = useState("0.00");
  const [formReason, setFormReason] = useState("");
  const [formActions, setFormActions] = useState("");
  const [isSelectAction, setIsSelectAction] = useState(true);
  const [currentAmount, setCurrentAmount] = useState(
    isOrderManuallyAdjusted(order)
      ? order?.remittanceManualAdjustmentTotalAmount
      : order?.total?.gross?.amount
  );

  const adjustmentTypes = [
    {
      label: "No Collection - Rider False Completion",
      value: "No Collection - Rider False Completion"
    },
    {
      label: "No Collection - 3PL COD Remittance",
      value: "No Collection - 3PL COD Remittance"
    },
    {
      label: "Short Collection - Price Discrepancy",
      value: "Short Collection - Price Discrepancy"
    },
    {
      label: "Short Collection  - OOS, Failure of Tenant to Edit in SMOP",
      value: "Short Collection  - OOS, Failure of Tenant to Edit in SMOP"
    },
    {
      label: "Short Collection - Incomplete Item",
      value: "Short Collection - Incomplete Item"
    },
    {
      label: "Short Collection - Voucher Issue",
      value: "Short Collection - Voucher Issue"
    },
    {
      label: "Short Collection  - Store Order False Completion",
      value: "Short Collection  - Store Order False Completion"
    },
    {
      label: "Overcollection - Price Discrepancy",
      value: "Overcollection - Price Discrepancy"
    },
    {
      label: "Overcollection - OOS, Failure of Tenant to Edit in SMOP",
      value: "Overcollection - OOS, Failure of Tenant to Edit in SMOP"
    }
  ];

  const amountLabel = forDeduct
    ? "Amount to be Deducted"
    : "Amount to be Added";

  React.useEffect(() => {
    if (isOrderManuallyAdjusted(order)) {
      setCurrentAmount(order?.remittanceManualAdjustmentTotalAmount);
    } else {
      setCurrentAmount(order?.total?.gross?.amount);
    }
  }, [order]);

  const handleConfirmSubmit = (reason, amount) => {
    const numericAmount = Number(amount);
    if (!amount || isNaN(numericAmount) || numericAmount === 0) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to Manually Adjust Order. Please provide the amount for adjustment."
        })
      });
      return;
    }

    if (!reason) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to Manually Adjust Order. Please provide the reason for adjustment."
        })
      });
      return;
    }

    if (forDeduct && amount > currentAmount) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Please enter a lower amount than the current total amount"
        })
      });
      return;
    }

    saveManualAdjustment({
      variables: {
        adjustedAmount: forDeduct ? amount * -1 : amount,
        adjustmentReason: reason,
        id: order.id
      }
    });
  };

  const [saveManualAdjustment] = useRemittanceRunnerManualAdjustment({
    onCompleted: data => {
      if (data?.createRemittanceManualAdjustment?.errors?.length === 0) {
        notify({
          status: "success",
          text: "Order Successfully Adjusted"
        });
        setIsSelectAction(true);
        setFormActions("");
        onClose();
        refetchOrder();
      } else if (data?.createRemittanceManualAdjustment?.errors?.length > 0) {
        data.createRemittanceManualAdjustment.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  React.useEffect(() => {
    if (open) {
      setForDeduct(true);
      setFormAmount("0.00");
      setFormReason("");
    }
  }, [open]);

  const selectActions = [
    {
      label: "Deduct Amount",
      value: "Deduct Amount"
    },
    {
      label: "Increase Amount",
      value: "Increase Amount"
    }
  ];

  React.useEffect(() => {
    if (formActions === "Deduct Amount") {
      setForDeduct(true);
      setIsSelectAction(false);
    }
    if (formActions === "Increase Amount") {
      setForDeduct(false);
      setIsSelectAction(false);
    }
  }, [formActions]);

  const closeModal = () => {
    setIsSelectAction(true);
    setFormActions("");
    onClose();
  };

  return (
    <Dialog onClose={closeModal} open={open} maxWidth="sm" fullWidth>
      <DialogContent className={classes.modal}>
        <IconButton
          aria-label="close"
          onClick={() => closeModal()}
          className={classes.modalCloseBtn}
        >
          <CloseIcon />
        </IconButton>

        <div className={classes.marginTop50}>
          <h3 className={`${classes.modalTitleText} ${classes.modalTitleRed}`}>
            Remittance Adjustment
          </h3>
          <h4 className={classes.modalSubtitle}>
            Are you sure you want to change the total of Mother Order
          </h4>
          <h4 className={classes.modalSubtitle} style={{ fontWeight: 600 }}>
            {order?.orderCode}?
          </h4>
        </div>
        <div className={classes.modalForm}>
          <RemittanceManualAdjustmentDropdown
            placeholder="Select Action"
            disabled={false}
            choices={selectActions}
            name="adjustment"
            label={intl.formatMessage({
              defaultMessage: "Type of Adjustment"
            })}
            value={formActions}
            isRequired={true}
            onChange={value => setFormActions(value.target.value)}
          />
          <TextField
            InputLabelProps={{
              classes: {
                asterisk: classes.labelAsterisk
              },
              shrink: true
            }}
            required
            disabled={isSelectAction}
            fullWidth
            type="number"
            name="amount"
            inputProps={{ step: "0.01" }}
            label={amountLabel}
            value={formAmount}
            onChange={value => setFormAmount(value.target.value)}
          />

          <h4
            className={classes.modalSubtitle}
            style={{
              paddingTop: "8px",
              textAlign: "left"
            }}
          >
            Current Total Amount:
            <span style={{ fontWeight: 600, paddingLeft: "5px" }}>
              {currencyFormat(currentAmount)}
            </span>
          </h4>
          <RemittanceManualAdjustmentDropdown
            placeholder="Select Reason"
            disabled={isSelectAction}
            choices={adjustmentTypes}
            name="reason"
            label={intl.formatMessage({
              defaultMessage: "Adjustment Reason"
            })}
            value={formReason}
            isRequired={true}
            onChange={value => setFormReason(value.target.value)}
          />
        </div>
        <div className={classes.modalBottom}>
          <Button
            id="cancelDialogActionBtn"
            autoFocus
            onClick={closeModal}
            color="secondary"
            style={{ marginRight: 10 }}
            className={classes.btnCancel}
          >
            <span>Cancel</span>
          </Button>
          <Button
            disabled={isSelectAction}
            id="confirmDialogActionBtn"
            autoFocus
            onClick={() => handleConfirmSubmit(formReason, formAmount)}
            variant="contained"
            className={classes.btnYes}
          >
            <span className={classes.btnLblWhite}>Yes</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
RemittanceManualAdjustDialog.displayName = "RemittanceManualAdjustDialog";
export default RemittanceManualAdjustDialog;
